import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withNotification } from 'wrappers';
import { Container, Grid, Hidden } from '@material-ui/core';
import {
    Loader, BatchPdfViewer, BaseModal, BaseModalError, ButtonBase, HeaderBase,
} from 'components';
import {
    abreviaturas, messages, masterMessages, masterEvents,
} from 'constantes';
import endPoints from 'endPoints/endPoints';
import { makeStyles } from '@material-ui/core/styles';
import Cookie from 'js-cookie';
import {
    base64ToBlob, blobToBase64,
} from 'utils/Functions';
import { ReactComponent as DocCompleted } from 'assets/images/docCompleted.svg';
import { PdfViewerBusiness } from 'business/business/PdfViewerBusiness';
import { AnalyticsAmplitud } from 'utils/Analytics-Amplitud';
import authService from 'auth/AuthService';

// Estilos de la pagina
const useStyles = makeStyles(() => ({
    imgContainerModalDownload: {
        textAlign: 'center',
        width: '50%',
        height: '40%',
    },
    imgModal: {
        width: '70%',
        height: 'auto',
        marginBottom: 2,
    },
}));

function BatchPdfViewerPage(
    {
        appSuccess, appError, history, location,
    },
) {
    const classes = useStyles();
    // Obtener IDs de la URL (formato: ?ids=123,456,789)
    const searchParams = new URLSearchParams(location.search);
    const solicitudIds = searchParams.get('ids')?.split(',').filter(Boolean) || [];

    // Mensajes
    const {
        pdfViewer: {
            modalDocProceso, modalDocSuccessMessages,
        },
        buttons,
    } = masterMessages;

    const { pdfViewer } = endPoints || {};

    const [loading, setLoading] = useState(true);
    const [pdfFiles, setPdfFiles] = useState([]);
    const [modalErrorProceso, setModalErrorProceso] = useState(false);
    const [modalDocSuccess, setModalDocSuccess] = useState(false);
    const [signedDocIds, setSignedDocIds] = useState([]);
    const [signedCount, setSignedCount] = useState(0);

    // Cerrar modales
    const handleCloseModal = () => {
        setModalErrorProceso(false);
        setModalDocSuccess(false);
    };

    /**
     * @name getDocFirmaBinary
     * @desc Consulta un documento al api y devuelve un binario
     * @param {string} solicitudId
     * @returns {Promise<{id: string, blob: Blob, name: string}|null>}
     */
    const getDocFirmaBinary = async (solicitudId) => {
        try {
            const res = await PdfViewerBusiness.getDocumentReader(solicitudId);
            if (res.status !== abreviaturas.DEFAULT_HTTP_FAIL[0]) {
                const dataText = await res.text();
                const blobPdf = await base64ToBlob(dataText);
                return {
                    id: solicitudId,
                    blob: blobPdf,
                    name: `Documento_${solicitudId}.pdf`,
                };
            }
            return null;
        } catch (e) {
            return null;
        }
    };

    /**
     * @name loadAllDocuments
     * @desc Valida permisos para todos los IDs y carga solo los autorizados
     */
    const loadAllDocuments = async () => {
        try {
            setLoading(true);

            // Paso 1: Validar permisos enviando todos los IDs concatenados
            // El servicio responde 200 con [id1, id2] para los válidos,
            // o 400 si ninguno es válido
            const validateRes = await PdfViewerBusiness.validatePermission(
                solicitudIds.join(','),
            );

            let validIds = [];
            if (
                validateRes.status === abreviaturas.DEFAULT_HTTP_OK[0]
                || validateRes.status === abreviaturas.DEFAULT_HTTP_OK[1]
            ) {
                const resText = await validateRes.text();
                const parsed = JSON.parse(resText); // e.g. [123, 456]
                validIds = parsed.map((id) => String(id));
            } else {
                // 400: el usuario no tiene acceso a ninguno de los documentos
                setModalErrorProceso(true);
                setLoading(false);
                return;
            }

            if (validIds.length === 0) {
                setModalErrorProceso(true);
                setLoading(false);
                return;
            }

            // Si solo hay un documento válido, redirigir al visor individual
            if (validIds.length === 1) {
                history.replace(`/apiv1/pdf-viewer/${validIds[0]}`);
                return;
            }

            // Paso 2: Cargar solo los documentos a los que tiene permiso
            const documentPromises = validIds.map((id) => getDocFirmaBinary(id));
            const documents = await Promise.all(documentPromises);
            const loadedDocuments = documents.filter((doc) => doc !== null);

            if (loadedDocuments.length === 0) {
                setModalErrorProceso(true);
                setLoading(false);
                return;
            }

            // Si tras cargar solo quedó un documento, usar el visor individual
            if (loadedDocuments.length === 1) {
                history.replace(`/apiv1/pdf-viewer/${loadedDocuments[0].id}`);
                return;
            }

            setPdfFiles(loadedDocuments);
            AnalyticsAmplitud.registerEvent(masterEvents.EVENTO_CARGA_PDF);
            setLoading(false);
        } catch (e) {
            setLoading(false);
            appError(messages.dataFetch.fail);
        }
    };

    useEffect(() => {
        if (solicitudIds.length === 0) {
            setModalErrorProceso(true);
            setLoading(false);
            return;
        }

        AnalyticsAmplitud.registerEvent(masterEvents.EVENTO_VISTA_PDF);
        loadAllDocuments();
    }, []);

    /**
     * @name mapValues
     * @desc Compone objeto de datos a enviar al api
     * @param {string} fileB64
     * @returns {Promise<Object>}
     */
    const mapValues = async (fileB64, selfieData) => {
        let ip = null;
        let loc = null;

        try {
            await fetch(pdfViewer.geoAltern)
                .then((response) => {
                    if (!response.ok) throw new Error('Error geoAltern');
                    return response.text();
                })
                .then((result) => {
                    const { ip: dirIp } = JSON.parse(result) || {};
                    ip = dirIp || '0.0.0.0';
                    loc = '';
                });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error al obtener geolocalización:', error.message);
        }

        const infoSession = await authService.getSessionInfo();
        const { t: token } = infoSession || {};
        const nombre = Cookie.get(abreviaturas.SIDUSUARIO);
        const otpNumber = Cookie.get(abreviaturas.DEFAULT_OTP_NUMBER);

        let selfieB64 = '';
        if (selfieData) {
            const parts = selfieData.split(',');
            selfieB64 = parts[1] || '';
        }

        const dataFile = {
            base64Document: fileB64,
            campos: [
                { nombreCampo: 'Dirección IP', valorCampo: ip || '0.0.0.0' },
                { nombreCampo: 'Locación (Pais)', valorCampo: loc || '' },
                { nombreCampo: 'Latitud', valorCampo: '' },
                { nombreCampo: 'Longitud', valorCampo: '' },
                { nombreCampo: 'Token', valorCampo: token || '' },
                { nombreCampo: 'Nombre usuario', valorCampo: nombre || '' },
                { nombreCampo: 'Código OTP', valorCampo: otpNumber || '' },
                { nombreCampo: 'Foto Selfie', valorCampo: selfieB64 },
            ],
        };

        return dataFile;
    };

    /**
     * @name onSaveAllPdfs
     * @desc Guarda todos los PDFs firmados usando el mismo endpoint
     * @param {Array<{id: string, blob: Blob, name: string}>} signedDocuments
     * Array de documentos firmados
     * @returns {Array<{id: string, success: boolean}>} - Resultados por documento
     */
    const onSaveAllPdfs = async (signedDocuments, selfieData) => {
        setLoading(true);

        const results = [];
        let successCount = 0;
        let failCount = 0;

        // Procesar cada documento firmado
        for (let i = 0; i < signedDocuments.length; i += 1) {
            const { id, blob } = signedDocuments[i];
            // Buscar el solicitudId correspondiente (el indice del doc-X coincide con pdfFiles)
            const docIndex = parseInt(id.replace('doc-', ''), 10);
            const solicitudId = pdfFiles[docIndex]?.id;

            if (!solicitudId) {
                results.push({ id, success: false });
                failCount += 1;
                // eslint-disable-next-line no-continue
                continue;
            }

            try {
                // Convertir a base64
                // eslint-disable-next-line no-await-in-loop
                const dataB64 = await blobToBase64(blob);
                const fileB64 = dataB64.split(abreviaturas.DEFAULT_TYPE_CONTENT_B64);

                // Preparar datos con auditoria
                // eslint-disable-next-line no-await-in-loop
                const data = await mapValues(fileB64[1], selfieData);

                // Enviar al servidor usando el mismo endpoint
                // eslint-disable-next-line no-await-in-loop
                const res = await PdfViewerBusiness.sendDocumentReader(data, solicitudId);

                if (res.status === abreviaturas.DEFAULT_HTTP_OK[1]) {
                    results.push({ id, success: true });
                    successCount += 1;
                } else {
                    results.push({ id, success: false });
                    failCount += 1;
                }
            } catch (e) {
                results.push({ id, success: false });
                failCount += 1;
            }
        }

        // Mostrar resultado final
        if (successCount > 0) {
            AnalyticsAmplitud.registerEvent(masterEvents.EVENTO_GUARDAR_FIRMA);
            appSuccess(`${successCount} documento(s) guardado(s) exitosamente`);
        }
        if (failCount > 0) {
            AnalyticsAmplitud.registerEvent(masterEvents.EVENTO_ERROR_FIRMA);
            appError(`${failCount} documento(s) no se pudieron guardar`);
        }

        // Guardar los IDs de documentos firmados exitosamente
        const successfulIds = results
            .filter((r) => r.success)
            .map((r) => {
                const docIndex = parseInt(r.id.replace('doc-', ''), 10);
                return pdfFiles[docIndex]?.id;
            })
            .filter(Boolean);
        setSignedDocIds(successfulIds);
        setSignedCount(successCount);

        setLoading(false);

        if (successCount > 0) {
            setModalDocSuccess(true);
        }

        // Retornar resultados para que el componente actualice estados
        return results;
    };

    /**
     * @name onClickModalExit
     * @desc Cierra sesion y redirige a login
     */
    const onClickModalExit = async () => {
        authService.logout();
        AnalyticsAmplitud.registerEvent(masterEvents.EVENTO_TERMINAT_SESION);
        history.push('/apiv1/sign-in');
    };

    /**
     * @name handleContinueWithUnsigned
     * @desc Continua firmando solo los documentos que no fueron firmados
     */
    const handleContinueWithUnsigned = () => {
        // Filtrar y mantener solo los documentos que no fueron firmados
        setPdfFiles((prev) => prev.filter((doc) => !signedDocIds.includes(doc.id)));
        setModalDocSuccess(false);
        setSignedDocIds([]);
        setSignedCount(0);
    };

    // Preparar archivos para el componente (solo los blobs)
    const pdfFilesForComponent = pdfFiles.map((doc) => {
        const file = doc.blob;
        file.name = doc.name;
        return file;
    });

    return loading ? <Loader /> : (
      <>
        {Boolean(pdfFilesForComponent.length > 0) && (
          <>
            <HeaderBase />
            <BatchPdfViewer
              pdfFiles={pdfFilesForComponent}
              onHandleComplete={onSaveAllPdfs}
            />
          </>
        )}

        {/* Modal de Error */}
        {Boolean(modalErrorProceso) && (
          <BaseModalError
            handleCloseModal={handleCloseModal}
            messages={modalDocProceso}
            maxWidth="sm"
          />
        )}

        {/* Modal de exito */}
        {Boolean(modalDocSuccess) && (
          <BaseModal
            open
            onClose={handleCloseModal}
            title={modalDocSuccessMessages.title}
            maxWidth="sm"
            close={false}
          >
            <Grid container>
              <Hidden only={['xs', 'md']}>
                <Grid item sm={12} md={12} xl={12}>
                  <Container className={classes.imgContainerModalDownload}>
                    <DocCompleted className={classes.imgModal} />
                  </Container>
                </Grid>
              </Hidden>
              <Grid item xs={12} md={12}>
                <p>
                  Se han firmado y guardado
                  {' '}
                  {signedCount}
                  {' '}
                  de
                  {' '}
                  {pdfFiles.length}
                  {' '}
                  documentos exitosamente.
                </p>
                {signedCount < pdfFiles.length && (
                  <p style={{ color: '#F44336', fontSize: '0.875rem' }}>
                    {pdfFiles.length - signedCount}
                    {' '}
                    documento(s) no tenían firma posicionada.
                  </p>
                )}
              </Grid>
              <Grid container alignItems="center" justifyContent="center" spacing={2}>
                <Grid item xs={6} md={6}>
                  <ButtonBase
                    caption={buttons.exit}
                    colorBase={abreviaturas.DEFAULT_RED_COLOR}
                    onClickEvent={onClickModalExit}
                  />
                </Grid>
                {signedCount < pdfFiles.length && (
                  <Grid item xs={6} md={6}>
                    <ButtonBase
                      caption="Firmar pendientes"
                      colorBase={abreviaturas.DEFAULT_WHITE_COLOR}
                      onClickEvent={handleContinueWithUnsigned}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </BaseModal>
        )}
      </>
    );
}

BatchPdfViewerPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
        replace: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({
        search: PropTypes.string,
    }).isRequired,
    appSuccess: PropTypes.func.isRequired,
    appError: PropTypes.func.isRequired,
};

export default withNotification(BatchPdfViewerPage);
